import { Fragment, ReactNode } from "react";
import { stripToolCallJsonDisplay } from "@/core/services/deepseek/parseToolCallsFromText";

type TableBlock = {
  type: "table";
  header: string[];
  rows: string[][];
};

type ParagraphBlock = {
  type: "paragraph";
  lines: string[];
};

type ContentBlock = TableBlock | ParagraphBlock;

type ParaPiece =
  | { type: "heading"; level: number; text: string }
  | { type: "hr" }
  | { type: "ul"; items: string[] }
  | { type: "ol"; items: string[] }
  | { type: "p"; lines: string[] };

function isTableLine(trimmed: string): boolean {
  if (!trimmed.startsWith("|")) return false;
  return (trimmed.match(/\|/g) ?? []).length >= 2;
}

function splitRow(line: string): string[] {
  const raw = line.trim();
  const inner = raw.startsWith("|") && raw.endsWith("|") ? raw.slice(1, -1) : raw.replace(/^\|/, "");
  return inner.split("|").map((c) => c.trim());
}

function isSeparatorRow(cells: string[]): boolean {
  if (cells.length === 0) return false;
  return cells.every((c) => /^:?-{2,}:?$/.test(c.replace(/\s/g, "")));
}

function padRow(cells: string[], len: number): string[] {
  const next = [...cells];
  while (next.length < len) next.push("");
  return next.slice(0, len);
}

function buildTable(lines: string[]): { header: string[]; rows: string[][] } | null {
  if (lines.length < 1) return null;
  const header = splitRow(lines[0]);
  if (header.length === 0) return null;

  let rowStart = 1;
  if (lines.length > 1) {
    const second = splitRow(lines[1]);
    if (isSeparatorRow(second)) rowStart = 2;
  }

  const colCount = header.length;
  const rows = lines.slice(rowStart).map((ln) => padRow(splitRow(ln), colCount));
  return { header, rows };
}

function parseBlocks(content: string): ContentBlock[] {
  const lines = content.split("\n");
  const blocks: ContentBlock[] = [];
  let i = 0;
  let para: string[] = [];

  const flushPara = () => {
    if (para.length === 0) return;
    blocks.push({ type: "paragraph", lines: [...para] });
    para = [];
  };

  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();

    if (isTableLine(trimmed)) {
      flushPara();
      const tableLines: string[] = [];
      while (i < lines.length && isTableLine(lines[i].trim())) {
        tableLines.push(lines[i].trim());
        i++;
      }
      const parsed = buildTable(tableLines);
      if (parsed) {
        blocks.push({ type: "table", ...parsed });
      } else {
        blocks.push({ type: "paragraph", lines: tableLines });
      }
      continue;
    }

    if (trimmed === "") {
      flushPara();
      i++;
      continue;
    }

    para.push(line);
    i++;
  }
  flushPara();
  return blocks;
}

/** Nhận một khối dòng (giữa các bảng / dòng trống) → heading, list, đoạn. */
function parseParagraphChunk(lines: string[]): ParaPiece[] {
  const out: ParaPiece[] = [];
  let buf: string[] = [];
  let ul: string[] | null = null;
  let ol: string[] | null = null;

  const flushBuf = () => {
    if (buf.length === 0) return;
    out.push({ type: "p", lines: [...buf] });
    buf = [];
  };
  const flushUl = () => {
    if (ul && ul.length) {
      out.push({ type: "ul", items: ul });
      ul = null;
    }
  };
  const flushOl = () => {
    if (ol && ol.length) {
      out.push({ type: "ol", items: ol });
      ol = null;
    }
  };

  for (const raw of lines) {
    const trimmed = raw.trim();

    const hm = trimmed.match(/^(#{1,6})\s+(.*)$/);
    if (hm) {
      flushUl();
      flushOl();
      flushBuf();
      out.push({ type: "heading", level: hm[1].length, text: hm[2] });
      continue;
    }

    if (/^(?:[-*_]){3,}\s*$/.test(trimmed)) {
      flushUl();
      flushOl();
      flushBuf();
      out.push({ type: "hr" });
      continue;
    }

    const ulm = trimmed.match(/^[-*+]\s+(.*)$/);
    if (ulm) {
      flushOl();
      flushBuf();
      if (!ul) ul = [];
      ul.push(ulm[1]);
      continue;
    }

    const olm = trimmed.match(/^\d+\.\s+(.*)$/);
    if (olm) {
      flushUl();
      flushBuf();
      if (!ol) ol = [];
      ol.push(olm[1]);
      continue;
    }

    flushUl();
    flushOl();
    buf.push(raw);
  }
  flushUl();
  flushOl();
  flushBuf();
  return out;
}

function renderInline(text: string): ReactNode {
  return <>{parseInlineToNodes(text)}</>;
}

function parseInlineToNodes(input: string): ReactNode[] {
  const out: ReactNode[] = [];
  let rest = input;
  let ni = 0;

  while (rest.length > 0) {
    const boldAt = rest.indexOf("**");
    const tickAt = rest.indexOf("`");
    const linkMatch = rest.match(/\[([^\]]*)\]\(([^)]*)\)/);
    const linkAt = linkMatch?.index ?? -1;

    const candidates: { kind: "bold" | "code" | "link"; at: number; len?: number; m?: RegExpMatchArray }[] = [];
    if (boldAt !== -1) candidates.push({ kind: "bold", at: boldAt });
    if (tickAt !== -1) candidates.push({ kind: "code", at: tickAt });
    if (linkAt !== -1 && linkMatch) candidates.push({ kind: "link", at: linkAt, m: linkMatch });

    if (candidates.length === 0) {
      out.push(<Fragment key={ni++}>{rest}</Fragment>);
      break;
    }

    candidates.sort((a, b) => a.at - b.at);
    const first = candidates[0];

    if (first.at > 0) {
      out.push(<Fragment key={ni++}>{rest.slice(0, first.at)}</Fragment>);
    }

    rest = rest.slice(first.at);

    if (first.kind === "bold") {
      const end = rest.indexOf("**", 2);
      if (end === -1) {
        out.push(<Fragment key={ni++}>{rest}</Fragment>);
        break;
      }
      const inner = rest.slice(2, end);
      out.push(
        <strong key={ni++} className="font-semibold text-slate-900">
          {parseInlineToNodes(inner)}
        </strong>
      );
      rest = rest.slice(end + 2);
      continue;
    }

    if (first.kind === "code") {
      const end = rest.indexOf("`", 1);
      if (end === -1) {
        out.push(<Fragment key={ni++}>{rest}</Fragment>);
        break;
      }
      const inner = rest.slice(1, end);
      out.push(
        <code
          key={ni++}
          className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[0.9em] text-slate-800"
        >
          {inner}
        </code>
      );
      rest = rest.slice(end + 1);
      continue;
    }

    if (first.kind === "link" && first.m) {
      const label = first.m[1];
      const href = first.m[2].trim();
      out.push(
        <a
          key={ni++}
          href={href || "#"}
          target="_blank"
          rel="noopener noreferrer"
          className="font-medium text-blue-600 underline decoration-blue-300 underline-offset-2 hover:text-blue-800"
        >
          {parseInlineToNodes(label)}
        </a>
      );
      rest = rest.slice(first.m[0].length);
      continue;
    }

    break;
  }

  return out;
}

function MarkdownTable({ header, rows }: { header: string[]; rows: string[][] }) {
  return (
    <div className="my-3 -mx-1 overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm">
      <table className="w-full min-w-[520px] border-collapse text-sm">
        <thead>
          <tr className="border-b border-slate-200 bg-slate-50">
            {header.map((h, hi) => (
              <th key={hi} className="px-3 py-2.5 text-left text-sm font-semibold text-slate-700">
                {renderInline(h)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => (
            <tr
              key={ri}
              className="border-b border-slate-100 last:border-b-0 odd:bg-white even:bg-slate-50/60"
            >
              {row.map((cell, ci) => (
                <td key={ci} className="px-3 py-2 text-slate-800">
                  {renderInline(cell)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const headingClass: Record<number, string> = {
  1: "text-xl font-bold text-slate-900 mt-1 mb-2",
  2: "text-lg font-semibold text-slate-900 mt-1 mb-1.5",
  3: "text-base font-semibold text-slate-900 mt-0.5 mb-1",
  4: "text-base font-semibold text-slate-800",
  5: "text-sm font-semibold text-slate-800",
  6: "text-sm font-semibold text-slate-700",
};

function ParagraphChunk({ pieces }: { pieces: ParaPiece[] }) {
  return (
    <div className="space-y-3">
      {pieces.map((piece, pi) => {
        if (piece.type === "heading") {
          const lvl = Math.min(6, Math.max(1, piece.level));
          const Tag = `h${lvl}` as "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
          return (
            <Tag key={pi} className={headingClass[lvl] ?? headingClass[3]}>
              {renderInline(piece.text)}
            </Tag>
          );
        }
        if (piece.type === "hr") {
          return <hr key={pi} className="my-3 border-0 border-t border-slate-200" />;
        }
        if (piece.type === "ul") {
          return (
            <ul key={pi} className="my-1 list-disc space-y-1 pl-5 text-slate-800">
              {piece.items.map((item, ii) => (
                <li key={ii} className="leading-relaxed">
                  {renderInline(item)}
                </li>
              ))}
            </ul>
          );
        }
        if (piece.type === "ol") {
          return (
            <ol key={pi} className="my-1 list-decimal space-y-1 pl-5 text-slate-800">
              {piece.items.map((item, ii) => (
                <li key={ii} className="leading-relaxed [&::marker]:font-medium">
                  {renderInline(item)}
                </li>
              ))}
            </ol>
          );
        }
        return (
          <div key={pi} className="space-y-1.5">
            {piece.lines.map((line, li) => (
              <p key={li} className="mb-0 whitespace-pre-wrap break-words leading-relaxed">
                {renderInline(line)}
              </p>
            ))}
          </div>
        );
      })}
    </div>
  );
}

interface Props {
  content: string;
}

/** Hiển thị nội dung trợ lý: bảng, # tiêu đề, list, gạch ngang, **đậm**, `code`, [link](url). */
export default function AssistantMessageContent({ content }: Props) {
  if (!content) return null;
  const cleaned = stripToolCallJsonDisplay(content);
  if (!cleaned) return null;
  const blocks = parseBlocks(cleaned);
  return (
    <div className="space-y-3">
      {blocks.map((block, bi) =>
        block.type === "table" ? (
          <MarkdownTable key={bi} header={block.header} rows={block.rows} />
        ) : (
          <ParagraphChunk key={bi} pieces={parseParagraphChunk(block.lines)} />
        )
      )}
    </div>
  );
}
